import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable, throwError, BehaviorSubject, filter, take, switchMap, catchError } from "rxjs";
import { Router } from "@angular/router";
import { AuthService } from "../services/auth.service";

type ApiError = {
  status: number;
  message: string;
  raw?: unknown;
};

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshDone$ = new BehaviorSubject<string | null>(null);

  constructor(private auth: AuthService, private router: Router) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((err: unknown) => {
        if (!(err instanceof HttpErrorResponse)) {
          return throwError((): ApiError => ({ status: 0, message: "Error inesperado", raw: err }));
        }

        if (err.status === 0) {
          return throwError((): ApiError => ({ status: 0, message: "No hay conexión con el servidor", raw: err }));
        }

        if (err.status === 401) {
          // Si ya falló el refresh, no reintentar
          if (req.url.includes("/auth/refresh") || req.url.includes("/auth/login")) {
            this.auth.logout();
            this.router.navigateByUrl("/account/auth/login");
            return throwError((): ApiError => this.buildError(err));
          }
          return this.handle401(req, next, err);
        }

        if (err.status === 403) {
          // Opcional: redirigir a /forbidden
        }

        return throwError((): ApiError => this.buildError(err));
      })
    );
  }

  // Valor centinela: avisa a los requests que están esperando que el
  // refresh falló, para que no se queden colgados esperando un token que
  // nunca va a llegar (antes, si el refresh fallaba, cualquier otro
  // request que hubiera recibido 401 al mismo tiempo se quedaba "pending"
  // para siempre en el navegador — esto es lo que causaba que botones
  // como "Generar factura" se quedaran pegados en "Generando..." sin
  // ningún error visible).
  private static readonly REFRESH_FAILED = "__REFRESH_FAILED__";

  private handle401(
    req: HttpRequest<any>,
    next: HttpHandler,
    original: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshDone$.next(null);

      return this.auth.refreshTokens().pipe(
        switchMap((tokens) => {
          this.isRefreshing = false;
          this.refreshDone$.next(tokens.accessToken);
          return next.handle(this.addToken(req, tokens.accessToken));
        }),
        catchError(() => {
          this.isRefreshing = false;
          this.auth.logout();
          this.router.navigateByUrl("/account/auth/login");
          // Desbloquea a cualquiera que estuviera esperando este refresh.
          this.refreshDone$.next(ErrorInterceptor.REFRESH_FAILED);
          return throwError((): ApiError => this.buildError(original));
        })
      );
    }

    // Otro request que también recibió 401 mientras se estaba refrescando: espera
    return this.refreshDone$.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        if (token === ErrorInterceptor.REFRESH_FAILED) {
          return throwError((): ApiError => this.buildError(original));
        }
        return next.handle(this.addToken(req, token!));
      })
    );
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private buildError(err: HttpErrorResponse): ApiError {
    const apiMessage = (err.error as any)?.message;
    const message = Array.isArray(apiMessage)
      ? apiMessage.join(", ")
      : typeof apiMessage === "string"
      ? apiMessage
      : err.statusText || "Error";
    return { status: err.status, message, raw: err };
  }
}
